"use client";

import { FormEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.12,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);
  return {
    ref,
    isVisible
  };
}
function RevealSection({
  children,
  className = ""
}: {
  children: ReactNode;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.08
  });
  return <div ref={ref} className={`${className} transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
      {children}
    </div>;
}
function CtaButton({
  href,
  children,
  onClick,
  variant = "primary"
}: {
  href: string;
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  variant?: "primary" | "secondary";
}) {
  return <a href={href} onClick={onClick} className={`group relative inline-flex h-14 items-center justify-center overflow-hidden rounded-full border px-8 text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 ${variant === "primary" ? "border-white/10 bg-white text-slate-900 shadow-[0_12px_32px_rgba(15,23,42,0.22)] hover:shadow-[0_18px_42px_rgba(15,23,42,0.28)]" : "border-white/16 bg-white/10 text-white shadow-[0_12px_28px_rgba(15,23,42,0.14)] backdrop-blur-sm hover:border-white/28 hover:bg-white/14 hover:shadow-[0_18px_40px_rgba(15,23,42,0.22)]"}`}>
      <span className="relative z-10">{children}</span>
    </a>;
}
function SectionBadge({
  children
}: {
  children: ReactNode;
}) {
  return <div className="mb-5 inline-flex items-center rounded-full border border-white/55 bg-white/40 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-md sm:text-xs">
      {children}
    </div>;
}
function SectionTitle({
  title,
  accent,
  light = false
}: {
  title: string;
  accent?: string;
  light?: boolean;
}) {
  return <h2 className={`font-default text-3xl font-bold tracking-[-0.03em] sm:text-4xl md:text-5xl ${light ? "text-white" : "text-slate-950"}`}>
      {title}{" "}
      {accent ? <span className="text-[#166534]">{accent}</span> : null}
    </h2>;
}
function ServiceCard({
  title,
  text,
  note,
  dark = false
}: {
  title: string;
  text: string;
  note?: string;
  dark?: boolean;
}) {
  return <article className={`rounded-[32px] border p-7 shadow-[0_14px_40px_rgba(15,23,42,0.08)] ${dark ? "border-white/12 bg-white/[0.08] text-white backdrop-blur-xl" : "border-slate-200/70 bg-white/85 text-slate-950 backdrop-blur-md"}`}>
      <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${dark ? "bg-white/[0.08]" : "bg-[#166534]/[0.06]"}`}>
        <span className={`text-xl ${dark ? "text-[#d8c27a]" : "text-[#166534]"}`}>
          •
        </span>
      </div>

      <h3 className={`mb-4 font-default text-xl font-semibold tracking-[-0.02em] ${dark ? "text-white" : "text-slate-950"}`}>
        {title}
      </h3>

      <p className={`font-default text-[15px] leading-7 ${dark ? "text-white/82" : "text-slate-700"}`}>
        {text}
      </p>

      {note ? <p className={`mt-4 border-t pt-4 font-default text-sm leading-6 ${dark ? "border-white/10 text-white/70" : "border-slate-200 text-slate-600"}`}>
          {note}
        </p> : null}
    </article>;
}
const sportAndMovement = [{
  title: "Обучение через движение",
  text: "Базовая двигательная подготовка помогает ребёнку лучше чувствовать своё тело, развивать координацию, моторику, выносливость и правильные двигательные реакции. Через движение усиливаются внимание, самовыражение, сотрудничество и способность использовать речь, жесты и мимику."
}, {
  title: "Баскетбол",
  text: "Баскетбол развивает гибкость, реакцию, внимание и понимание цели. Игра укрепляет мышечную и костную систему, повышает уверенность в себе и помогает лучше понимать правила и границы."
}, {
  title: "Велосипед",
  text: "Велосипед развивает равновесие, координацию, связь «рука-глаз», самостоятельность и уверенность. Также он помогает уменьшать стереотипное поведение и поддерживает развитие социальных навыков."
}, {
  title: "Ролики",
  text: "Катание на роликах развивает равновесие, координацию, гибкость тела и уверенность в себе. Во время занятий ребёнок учится концентрироваться и одновременно отслеживать несколько объектов."
}, {
  title: "Упражнения",
  text: "Совместные упражнения помогают развивать зрительный контакт, внимание, взаимодействие один на один и социальные навыки. Занятия также снижают стереотипное поведение и поддерживают естественное развитие."
}, {
  title: "Гимнастика",
  text: "Гимнастика развивает гибкость, силу, координацию, выносливость и ловкость. Она помогает ребёнку лучше чувствовать и контролировать своё тело, повышая самооценку и уверенность."
}, {
  title: "Фитнес",
  text: "Занятия на тренажёрах и с безопасной нагрузкой развивают ловкость, реакцию, дисциплину, внимательность и выносливость. Они помогают укреплять нейронные связи, влияя на развитие интеллекта и речи."
}, {
  title: "Кроссфит",
  text: "Детский кроссфит в игровом формате помогает укреплять мышцы и суставы, улучшать координацию и общее физическое состояние. Он помогает расходовать энергию, улучшает сон, самооценку и социальные навыки."
}, {
  title: "Атлетика",
  text: "Лёгкая атлетика укрепляет мышцы, кости и сердце, повышает гибкость и выносливость. Она поддерживает физическое и умственное развитие ребёнка и усиливает уверенность в себе."
}, {
  title: "Настольный теннис",
  text: "Настольный теннис развивает концентрацию, скорость реакции, ориентацию в пространстве и навык быстро принимать решения. Также он улучшает зрительный контакт, усидчивость и терпение."
}];
const therapyAndSpecialists = [{
  title: "Иппотерапия",
  text: "Иппотерапия помогает ребёнку развиваться физически, эмоционально и социально. Она улучшает моторику, равновесие, речь, уверенность, самостоятельность и сенсорную интеграцию, а также снижает напряжение и тревожность.",
  note: "Это дополнительная услуга, а простое катание на лошадях входит в пакет."
}, {
  title: "Спортивный массаж",
  text: "Спортивный массаж помогает выявить напряжённые и слабые зоны, выстроить безопасную траекторию упражнений и повысить телесную осознанность ребёнка. Семья получает рекомендации и краткий письменный отчёт для домашней практики.",
  note: "Спортивный массаж не заменяет медицинское лечение."
}, {
  title: "Психолог",
  text: "Работа с психологом помогает ребёнку регулировать эмоции, принимать подсказки взрослого, развивать навыки просьбы и отказа и снижать частоту кризисных реакций. Это облегчает адаптацию дома и в школьной среде."
}, {
  title: "Логопед",
  text: "Логопед помогает развивать понимание речи, словарный запас, построение фраз, артикуляцию и коммуникативные инициативы. При необходимости используется АДК — альтернативная и дополнительная коммуникация."
}, {
  title: "Психологическая поддержка родителей",
  text: "Работа с родителями помогает выстроить понятный домашний план, снизить стресс и сформировать спокойный подход к сложному поведению ребёнка. Семья получает стратегии, письменные рекомендации и единый подход к границам и подкреплению."
}];
const dailyAndSocial = [{
  title: "Самообслуживание",
  text: "Это направление помогает ребёнку освоить базовые бытовые навыки: одевание, раздевание, гигиену, использование приборов, чистку зубов, туалетный навык и другие ежедневные действия. Главная цель — научить ребёнка самостоятельно заботиться о себе."
}, {
  title: "Развивающая среда вне занятий",
  text: "Пикники, барбекю, огород, праздники и дни рождения помогают развивать самостоятельность, социальные навыки, сенсорную регуляцию, речь и чувство включённости в группу. Такая среда снижает тревожность и помогает лучше понимать социальные ритуалы и события."
}];
const waterAndNature = [{
  title: "Плавание",
  text: "Плавание помогает ребёнку расслабляться, укреплять мышцы, развивать координацию, внимание, уверенность в себе и контроль дыхания. Вода улучшает сенсорное восприятие и снижает тревожность."
}, {
  title: "Море",
  text: "Занятия на море помогают ребёнку преодолевать страх воды, социализироваться и спокойнее вести себя в открытой среде.",
  note: "Это сезонная летняя программа."
}, {
  title: "Трекинг в лесу",
  text: "Прогулки в лесу поддерживают психоэмоциональное здоровье, снижают стресс, развивают восприятие и навыки поведения в социуме. Организованная прогулка в группе побуждает ребёнка к общению и взаимодействию."
}];
const outdoorActivities = [{
  title: "Рафтинг",
  text: "Рафтинг направлен на социализацию ребёнка, развитие уверенности в себе и умение взаимодействовать с окружающими.",
  note: "Это сезонная летняя программа."
}, {
  title: "Сафари",
  text: "Сафари развивает сенсорный, двигательный и эмоциональный опыт ребёнка. Такая активность помогает укреплять уверенность, снижать тревожность, улучшать координацию и включённость во внешний мир."
}, {
  title: "Парашют",
  text: "Полет над морем вместе с тренером даёт ребёнку яркие эмоции, развивает уверенность в себе и помогает социализации.",
  note: "Это сезонная летняя активность, которая проходит в сопровождении наставника."
}, {
  title: "Зоопарк",
  text: "Поездка в зоопарк помогает развивать сенсорное восприятие, эмоциональный интеллект, навыки поведения в группе и уверенность в новых социальных ситуациях.",
  note: "Это зимняя программа."
}];
export default function ServicesPage() {
  const handleScrollToContact = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const form = document.getElementById("contact-form");
    form?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    window.location.href = "/thank-you";
  };
  return <main className="bg-[#f7f7f4] text-slate-950">
      <section className="relative overflow-hidden bg-[#0f172a] px-6 py-20 text-white md:px-12 md:py-24">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_34%)]" />
          <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-white/[0.03] blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#166534]/[0.08] blur-3xl" />
        </div>

        <RevealSection className="relative z-10 mx-auto max-w-7xl">
          <div className="max-w-4xl">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Услуги центра MONA
            </div>

            <h1 className="mb-6 font-default text-4xl font-bold tracking-[-0.04em] text-white sm:text-5xl md:text-6xl lg:text-7xl">
              Услуги центра <span className="text-[#166534]">MONA</span>
            </h1>

            <p className="mb-4 max-w-3xl font-default text-xl leading-relaxed text-white/84 md:text-2xl">
              Все направления развития, которые помогают ребёнку двигаться,
              адаптироваться, учиться и становиться увереннее.
            </p>

            <p className="mb-10 max-w-3xl font-default text-lg leading-relaxed text-white/72 md:text-xl">
              В MONA мы используем разные виды спорта, терапии и активности,
              чтобы развивать тело, внимание, самостоятельность, речь,
              поведение и социальные навыки ребёнка.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <CtaButton href="#contact-form" onClick={handleScrollToContact}>
                Записаться на курс
              </CtaButton>
              <CtaButton href="#contact-form" onClick={handleScrollToContact} variant="secondary">
                Оставить заявку
              </CtaButton>
            </div>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-white py-20 md:py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-[40px] border border-slate-200/70 bg-[#f8fafc] p-8 shadow-[0_14px_40px_rgba(15,23,42,0.06)] md:p-12">
            <SectionBadge>Что входит в систему развития MONA</SectionBadge>
            <SectionTitle title="Что входит в систему развития" accent="MONA" />
            <div className="mt-6 space-y-4 font-default text-lg leading-relaxed text-slate-700">
              <p>
                В центре собраны разные форматы занятий: от базовой двигательной
                подготовки до работы со специалистами, бытовых навыков и
                выездных активностей.
              </p>
              <p>
                Каждое направление помогает ребёнку решать свою задачу: лучше
                чувствовать тело, увереннее взаимодействовать с миром,
                спокойнее вести себя, развивать речь, навыки общения и
                самостоятельности.
              </p>
            </div>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#f7f7f4] py-20 md:py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mb-12">
            <SectionBadge>Категория</SectionBadge>
            <SectionTitle title="Спорт и" accent="движение" />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {sportAndMovement.map(item => <ServiceCard key={item.title} title={item.title} text={item.text} />)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#0f172a] py-20 text-white md:py-24">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.05),transparent_34%)]" />
          <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-white/[0.03] blur-3xl" />
        </div>

        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mb-12">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Категория
            </div>
            <SectionTitle title="Терапия и" accent="специалисты" light />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {therapyAndSpecialists.map(item => <ServiceCard key={item.title} title={item.title} text={item.text} note={item.note} dark />)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-white py-20 md:py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mb-12">
            <SectionBadge>Категория</SectionBadge>
            <SectionTitle title="Бытовые и социальные" accent="навыки" />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {dailyAndSocial.map(item => <ServiceCard key={item.title} title={item.title} text={item.text} />)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#f7f7f4] py-20 md:py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mb-12">
            <SectionBadge>Категория</SectionBadge>
            <SectionTitle title="Вода и" accent="природа" />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {waterAndNature.map(item => <ServiceCard key={item.title} title={item.title} text={item.text} note={item.note} />)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#0f172a] py-20 text-white md:py-24">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.05),transparent_34%)]" />
        </div>

        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mb-12">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Категория
            </div>
            <SectionTitle title="Выездные" accent="активности" light />
            <p className="mt-5 max-w-3xl font-default text-base leading-relaxed text-white/72 md:text-lg">
              Некоторые активности доступны сезонно и зависят от выбранного пакета.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {outdoorActivities.map(item => <ServiceCard key={item.title} title={item.title} text={item.text} note={item.note} dark />)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-white py-20 md:py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-[40px] border border-slate-200/70 bg-[#f8fafc] p-8 shadow-[0_14px_40px_rgba(15,23,42,0.06)] md:p-12">
            <SectionBadge>Важно знать</SectionBadge>
            <SectionTitle title="Важно" accent="знать" />
            <div className="mt-6 space-y-4 font-default text-lg leading-relaxed text-slate-700">
              <p>
                В зависимости от выбранного пакета и сезона перечень услуг может меняться.
              </p>
              <p>
                Администрация центра оставляет за собой право корректировать состав пакетов, добавляя или исключая отдельные активности.
              </p>
              <p>
                По всем вопросам, включая стоимость курса, необходимо обращаться к администратору.
              </p>
            </div>
          </div>
        </RevealSection>
      </section>

      <section id="contact-form" className="relative overflow-hidden bg-[#0f172a] px-6 py-20 text-white md:px-12 md:py-24">
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_34%)]" />
    <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-white/[0.03] blur-3xl" />
    <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#166534]/[0.08] blur-3xl" />
  </div>

  <RevealSection className="relative z-10 mx-auto max-w-7xl">
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr]">
      <div className="rounded-[40px] border border-white/10 bg-white/[0.05] p-8 backdrop-blur-md md:p-10">
        <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
          Подберём подходящий формат занятий
        </div>

        <h2 className="mb-6 font-default text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl md:text-5xl">
          Подберём подходящий
          <br />
          <span className="text-[#166534]">формат занятий</span>
        </h2>

        <p className="max-w-xl font-default text-lg leading-relaxed text-white/78">
          Мы поможем определить, какие активности и специалисты будут полезны
          именно вашему ребёнку.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-[40px] border border-white/10 bg-white/[0.08] p-8 shadow-[0_24px_60px_rgba(0,0,0,0.24)] backdrop-blur-xl md:p-10">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-white/80">
              Имя ребёнка
            </span>
            <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-white/80">Возраст</span>
            <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>

          <label className="flex flex-col gap-2 md:col-span-2">
            <span className="text-sm font-medium text-white/80">Диагноз</span>
            <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-white/80">Страна</span>
            <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-white/80">Телефон</span>
            <input required type="tel" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>

          <label className="flex flex-col gap-2 md:col-span-2">
            <span className="text-sm font-medium text-white/80">
              Желаемый месяц
            </span>
            <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
          </label>
        </div>

        <div className="mt-8">
          <button type="submit" className="group relative inline-flex h-14 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white px-10 text-base font-semibold text-slate-900 shadow-[0_12px_32px_rgba(15,23,42,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(15,23,42,0.28)]">
            Оставить заявку
          </button>
        </div>
      </form>
    </div>
  </RevealSection>
</section>

    </main>;
}